import {Component, EventEmitter, forwardRef, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DateRange} from '../../../types/date-range';

import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-date-range-selector',
  imports: [
    FormsModule,
    TranslatePipe
],
  templateUrl: './date-range-selector.component.html',
  styleUrl: './date-range-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeSelectorComponent),
      multi: true
    }
  ]
})
export class DateRangeSelectorComponent implements ControlValueAccessor, OnInit {

  @Input('date') dateSelector: string = 'this-month';
  @Input() initialFrom: string = '';
  @Input() initialTo: string = '';
  @Input('timezone') timezone: string = '';
  @Input('shortTimezone') shortTimezone: boolean = false;
  @Input('showTimezone') showTimezone: boolean = true;
  @Input('utc') utc: boolean = false;
  @Input('zone') zone: string = '';
  @Input() label: string = 'Period';
  @Input() inputId: string = 'period';
  @Output() dateChanged = new EventEmitter<DateRange>();
  @Output() init = new EventEmitter<DateRange>();

  dateFrom: Date = new Date();
  dateTo: Date = new Date();

  dateFromSelector: string = '';
  dateToSelector: string = '';

  ngOnInit() {
    if (this.zone) {
      this.showTimezone = false;
    }

    if (this.utc) {
      this.timezone = '0';
      this.showTimezone = false;
    }

    if (!this.timezone) {
      this.timezone = Math.round(-(new Date(2000, 0, 1)).getTimezoneOffset() / 60).toString();
    }

    if (this.isValidDate(this.initialFrom) && this.isValidDate(this.initialTo)) {
      this.dateSelector = 'custom';
      this.dateFromSelector = this.toDateSelector(this.initialFrom);
      this.dateToSelector = this.toDateSelector(this.initialTo);
    }

    this.onDateChange();

    this.init.emit({
      start: this.dateFrom,
      end: this.dateTo,
    });
  }

  hasDst(): boolean {
    let jan = (new Date(2000, 0, 1)).getTimezoneOffset();
    let jul = (new Date(2000, 6, 1)).getTimezoneOffset();
    return jan != jul;
  }

  isCurrentlyDST() {
    const now = new Date();

    const jan = new Date(now.getFullYear(), 0, 1);
    const jul = new Date(now.getFullYear(), 6, 1);

    const standardOffset = Math.max(
      jan.getTimezoneOffset(),
      jul.getTimezoneOffset()
    );

    return now.getTimezoneOffset() < standardOffset;
  }

  getTimezones(): number[] {
    return Array.from({length: 27}, (_, i) => i - 12);
  }

  getTimezoneOffsetLabel(timezone: number): string {
    let offset = (this.hasDst() && this.isCurrentlyDST()) ? timezone + 1 : timezone;

    let text = '';
    if (offset > 0) {
      text = ' +' + offset.toString();
    } else if (offset < 0) {
      text = ' ' + offset.toString();
    } else {
      text = '';
    }

    if (this.shortTimezone) {
      return 'GMT' + text;
    }

    return '(GMT' + text + ') ';
  }

  computeDate(): void {
    let dateFrom: Date = new Date(this.dateFromSelector);
    let dateTo: Date = new Date(this.dateToSelector);
    // In UTC/zone mode, shift "now" so its local calendar getters return the current date in that zone
    const now = this.zone
      ? new Date(Date.now() + ((new Date()).getTimezoneOffset() + this.getZoneOffsetMinutes(new Date())) * 60000)
      : this.utc ? new Date(Date.now() + (new Date()).getTimezoneOffset() * 60000) : new Date();

    if (this.dateSelector === 'custom') {
      if (!this.dateFromSelector || !this.dateToSelector || isNaN(dateFrom.getDate()) || isNaN(dateTo.getDate()) || dateTo.getTime() < dateFrom.getTime()) {
        // If date is invalid, switch to this month date
        this.dateSelector = 'this-month';
      } else {
        dateTo.setUTCHours(23);
        dateTo.setUTCMinutes(59);
        dateTo.setUTCSeconds(59);
        dateTo.setUTCMilliseconds(999);
      }
    }

    switch (this.dateSelector) {
      case 'today':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'yesterday':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999));
        break;
      case 'this-week':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-week':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 6));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay(), 23, 59, 59, 999));
        break;
      case 'this-month':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-month':
        const dayCount = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, dayCount, 23, 59, 59, 999));
        break;
      case 'this-year':
        dateFrom = new Date(Date.UTC(now.getFullYear(), 0, 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-year':
        dateFrom = new Date(Date.UTC(now.getFullYear() - 1, 0, 1));
        dateTo = new Date(Date.UTC(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999));
        break;
    }

    this.dateFromSelector = dateFrom.toISOString().split('T')[0];
    this.dateToSelector = dateTo.toISOString().split('T')[0];

    // Offsets are computed per bound: in zone mode a range can start in +01:00
    // (winter) and end in +02:00 (summer)
    this.dateFrom = new Date(dateFrom.toISOString().split('Z')[0] + this.getOffsetStringFor(dateFrom));
    this.dateTo = new Date(dateTo.toISOString().split('Z')[0] + this.getOffsetStringFor(dateTo));

    this.onChange({
      start: this.dateFrom,
      end: this.dateTo,
    });

    this.dateChanged.emit({
      start: this.dateFrom,
      end: this.dateTo,
    });
  }

  getTimezoneOffsetString(): string {
    if (this.utc) {
      return '+00:00';
    }

    const negativeTimezone = Number(this.timezone) < 0;
    const realTimezone = Number(this.timezone) + ((this.hasDst() && this.isCurrentlyDST()) ? 1 : 0);
    return (negativeTimezone ? '-' : '+') + Math.abs(Number(realTimezone)).toString().padStart(2, '0') + ':00';
  }

  private getOffsetStringFor(bound: Date): string {
    return this.zone ? this.getZoneOffsetString(bound) : this.getTimezoneOffsetString();
  }

  private getZoneOffsetString(instant: Date): string {
    const name = new Intl.DateTimeFormat('en-US', {timeZone: this.zone, timeZoneName: 'longOffset'})
      .formatToParts(instant)
      .find(part => part.type === 'timeZoneName')?.value ?? 'GMT';
    const offset = name.replace('GMT', '');

    return offset || '+00:00';
  }

  private getZoneOffsetMinutes(instant: Date): number {
    const match = this.getZoneOffsetString(instant).match(/^([+-])(\d{2}):(\d{2})$/);

    if (!match) {
      return 0;
    }

    return (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
  }

  onDateChange(): void {
    this.computeDate();
  }

  onCustomDateChange(): void {
    this.dateSelector = 'custom';

    this.onDateChange();
  }

  isCustomDate(): boolean {
    return this.dateSelector === 'custom';
  }

  getDateFrom(): Date {
    return this.dateFrom;
  }

  getDateTo(): Date {
    return this.dateTo;
  }

  getDateISO8601From(): string {
    return this.dateFromSelector + 'T00:00:00' + this.getOffsetStringFor(new Date(this.dateFromSelector));
  }

  getDateISO8601To(): string {
    return this.dateToSelector + 'T23:59:59' + this.getOffsetStringFor(new Date(this.dateToSelector + 'T23:59:59Z'));
  }

  isValidDate(date: any): boolean {
    return !!(date && !isNaN(Date.parse(date)));
  }

  /**
   * Reduce any parsable date (ISO 8601 with time, timestamp, ...) to the
   * yyyy-MM-dd form expected by the date inputs and by computeDate().
   */
  private toDateSelector(date: string): string {
    return new Date(date).toISOString().split('T')[0];
  }

  writeValue(obj: any): void {
    if (typeof obj === 'string') {
      this.dateSelector = obj;
    } else {
      if (this.isValidDate(obj?.from) && this.isValidDate(obj?.to)) {
        this.dateSelector = 'custom';
        this.dateFromSelector = obj?.from;
        this.dateToSelector = obj?.to;
      }
    }

    this.computeDate();
  }

  onChange = (_: any) => {
  };

  onBlur = (_: any) => {
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onBlur = fn;
  }
}
